import { type FC, useContext } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { BarHeader } from 'dibs-elements/exports/BarHeader.tsx';
import { ModalBody } from 'dibs-elements/exports/ModalBody.tsx';
import { EthereumConnectionZipCodeForm } from 'dibs-crypto-utils/exports/EthereumConnectionZipCodeForm.ts';
import { type ZipCodeOutput } from 'dibs-crypto-utils/exports/EthereumConnectionZipCodeForm.ts';
import { ethereumPaymentSectionUrl } from 'dibs-crypto-utils/exports/constants.ts';
import { nftFaqUrl } from 'dibs-crypto-utils/exports/constants.ts';
import { NftPdpBidModalContext } from '../NftPdpBidModalContext.tsx';
import { useWeb3Connection } from 'dibs-crypto-utils/src/useWeb3Connection.tsx';
import { type ZipCodeFormContent_viewer$key } from './__generated__/ZipCodeFormContent_viewer.graphql.ts';

import styles from './ZipCodeFormContent.scss';

export const ZipCodeFormContent: FC<{
    viewer: ZipCodeFormContent_viewer$key;
}> = ({ viewer: viewerRef }) => {
    const { connectToMetamaskForBidderRole } = useContext(NftPdpBidModalContext);
    const { connected } = useWeb3Connection();

    const viewer = useFragment(
        graphql`
            fragment ZipCodeFormContent_viewer on Viewer {
                ...EthereumConnectionZipCodeForm_viewer
            }
        `,
        viewerRef
    );

    const connectWithZipCode = async ({
        zipCode,
        isInternationalCustomer,
    }: ZipCodeOutput): Promise<void> => {
        await connectToMetamaskForBidderRole({ zipCode, isInternationalCustomer });
    };

    return (
        <>
            <BarHeader
                title={<FormattedMessage id="nft.pdp.bidModal.zipCode" defaultMessage="Zip Code" />}
            />
            <ModalBody className={styles.body}>
                <div className={styles.textWrapper}>
                    <FormattedMessage
                        id="nft.pdp.bidModal.zipCode.bodyCopy"
                        defaultMessage="United States Customers: before proceeding, please provide your zip code. 1stDibs uses this information only to comply with state sales tax requirements."
                    />
                </div>

                <EthereumConnectionZipCodeForm
                    onClick={connectWithZipCode}
                    isLoading={connected.connecting}
                    errorMessage={
                        connected.unknownError ? (
                            <FormattedMessage
                                id="nft.pdp.bidModal.zipCode.unexpectedError"
                                defaultMessage="Unexpected error occured. Try reconnecting Ethereum address in <a>payment settings</a>."
                                values={{
                                    a: chunk => (
                                        <Link
                                            dataTn="my-account-ethereum-link"
                                            href={ethereumPaymentSectionUrl}
                                            target="_blank"
                                            className={styles.errorLink}
                                        >
                                            {chunk}
                                        </Link>
                                    ),
                                }}
                            />
                        ) : null
                    }
                    viewer={viewer}
                />
                <div className={styles.footer}>
                    <Link href={nftFaqUrl} dataTn="nft-faq-link" key="nft-faq-link">
                        <FormattedMessage
                            id="nft.pdp.nftFaqLink"
                            defaultMessage="Learn More about NFTs on 1stDibs"
                        />
                    </Link>
                </div>
            </ModalBody>
        </>
    );
};
